import moment from 'moment';

export const timeFormat = (time?: string) => {
  if (!time) {
    return '';
  }
  const date = moment(time);
  const startOfDay = moment().startOf('day');
  if (date > startOfDay) {
    return date?.format('h:mm a');
  } else {
    return date?.format('MM/DD/YYYY');
  }
};

export const dateFormat = (time: string) => {
  return moment(new Date(time)).format('YYYY-MM-DD HH:mm');
};

/**
 * Relative timestamp for conversation list (e.g. "Just now", "2h ago",
 * "Yesterday", weekday for the last week, else short date). UI-only helper.
 */
export const relativeTimeFormat = (time?: string) => {
  if (!time) {
    return '';
  }
  const date = moment(time);
  if (!date.isValid()) {
    return '';
  }
  const now = moment();
  const diffMinutes = now.diff(date, 'minutes');
  const diffHours = now.diff(date, 'hours');

  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffHours < 1) {
    return `${diffMinutes}m ago`;
  }
  if (date.isSame(now, 'day')) {
    return `${diffHours}h ago`;
  }
  if (date.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }
  if (now.diff(date, 'days') < 7) {
    return date.format('ddd');
  }
  return date.format('MMM D');
};

/**
 * Date-bucket grouping for conversation/task lists (UI-only display helper).
 * Splits an already-loaded list into ordered buckets: Today, Yesterday,
 * Previous 7 days, then one bucket per older month (newest first).
 *
 * `key` is a stable i18n/render identifier:
 *   - 'today' | 'yesterday' | 'previous7days'  -> translate via chat.group.*
 *   - 'YYYY-MM' (e.g. '2026-05')               -> render `label` (e.g. "May 2026")
 * Items keep their incoming relative order within each bucket. Items with a
 * missing/invalid date fall into a trailing 'older' bucket.
 */
export interface DateBucket<T> {
  key: string;
  label: string;
  items: T[];
}

export const groupByDateBucket = <T>(
  items: T[] | undefined,
  getDate: (_item: T) => string | undefined
): DateBucket<T>[] => {
  if (!items?.length) {
    return [];
  }

  const now = moment();
  const startOfToday = now.clone().startOf('day');
  const startOfYesterday = startOfToday.clone().subtract(1, 'day');
  const startOf7DaysAgo = startOfToday.clone().subtract(6, 'day');

  const order: string[] = [];
  const map = new Map<string, DateBucket<T>>();

  const push = (key: string, label: string, item: T) => {
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { key, label, items: [] };
      map.set(key, bucket);
      order.push(key);
    }
    bucket.items.push(item);
  };

  items.forEach((item) => {
    const raw = getDate(item);
    const date = raw ? moment(raw) : null;

    if (!date || !date.isValid()) {
      push('older', '', item);
      return;
    }
    if (date.isSameOrAfter(startOfToday)) {
      push('today', '', item);
    } else if (date.isSameOrAfter(startOfYesterday)) {
      push('yesterday', '', item);
    } else if (date.isSameOrAfter(startOf7DaysAgo)) {
      push('previous7days', '', item);
    } else {
      push(date.format('YYYY-MM'), date.format('MMMM YYYY'), item);
    }
  });

  // Fixed buckets first (in mockup order), then month buckets newest-first,
  // then the trailing 'older' fallback bucket.
  const fixedOrder = ['today', 'yesterday', 'previous7days'];
  const monthKeys = order
    .filter((k) => !fixedOrder.includes(k) && k !== 'older')
    .sort((a, b) => (a < b ? 1 : -1));
  const sortedKeys = [
    ...fixedOrder.filter((k) => map.has(k)),
    ...monthKeys,
    ...(map.has('older') ? ['older'] : []),
  ];

  return sortedKeys.map((k) => map.get(k)!);
};
